import GuildContext, { useGuild } from 'app/frontend/base/config/context/guild'
import EntityMenu from 'app/frontend/base/components/EntityMenu'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { Link } from 'react-router-dom'
import { useTheme } from '@/components/theme-provider'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { useGuildMenuItems } from '../../GuildDashboard/components/GuildHeader'
import {
  CustomDomainSelectedTeamMenu,
  CustomDomainTeamMenu,
  CustomDomainTeamSwitcher,
  DEFAULT_CUSTOM_DOMAIN_MAIN_MENU_CONTENT,
  getSelectedCustomDomainTeam,
} from './CustomDomainTeamNavigation'

const CustomDomainHeader = () => {
  const { currentScheme } = useTheme()
  const { customDomain } = useCustomDomain()

  if (!customDomain) return null

  const headerLogoPath =
    currentScheme === 'dark'
      ? customDomain?.darkModeHeaderLogoPath
      : customDomain?.lightModeHeaderLogoPath

  return (
    <div className='flex min-w-0 flex-1 items-center gap-4'>
      <Link to='/' className='flex items-center gap-2 shrink-0'>
        {headerLogoPath ? (
          <img src={headerLogoPath} className='h-10 min-h-10 w-auto' />
        ) : (
          <BannerImage
            className='w-12 h-12 min-w-12 min-h-12'
            faction={customDomain.guild.faction}
            fileName={customDomain.guild.bannerFilename}
            size='custom'
          />
        )}

        {customDomain.headerShowName ? (
          <span className='text-xl font-bold text-accent-foreground max-sm:hidden'>
            {customDomain.guild.name}
          </span>
        ) : null}
      </Link>

      <div className='flex min-w-0 flex-1 items-center gap-0'>
        {customDomain.team ? (
          <GuildContext.Provider
            value={{
              guild: customDomain.guild,
              guildReference: customDomain.guild,
              currentTeam: customDomain.team,
              teamReference: customDomain.team,
            }}
          >
            <CustomDomainTeamMenu />
          </GuildContext.Provider>
        ) : (
          <GuildContext.Provider
            value={{
              guild: customDomain.guild,
              guildReference: customDomain.guild,
            }}
          >
            <CustomDomainGuildHeader />
          </GuildContext.Provider>
        )}
      </div>
    </div>
  )
}

const CustomDomainGuildHeader = () => {
  const { customDomain } = useCustomDomain()
  const mainMenuContent =
    customDomain?.headerConfig?.mainMenuContent ??
    DEFAULT_CUSTOM_DOMAIN_MAIN_MENU_CONTENT
  const selectedTeam = getSelectedCustomDomainTeam(customDomain)
  const showTeamSwitcher = mainMenuContent !== 'only_guild_menu'

  return (
    <GuildContext.Provider
      value={{
        guild: customDomain?.guild,
        guildReference: customDomain?.guild,
        teamReference: selectedTeam,
      }}
    >
      <div className='flex min-w-0 flex-1 items-center gap-4'>
        {showTeamSwitcher ? (
          <div className='shrink-0'>
            <CustomDomainTeamSwitcher
              selectedTeam={selectedTeam}
              showSelectedTeamLabel={true}
            />
          </div>
        ) : null}

        <CustomDomainGuildMenu mainMenuContent={mainMenuContent} />
      </div>
    </GuildContext.Provider>
  )
}

const CustomDomainGuildMenu = ({
  mainMenuContent,
}: {
  mainMenuContent: wowaudit.CustomDomain['headerConfig']['mainMenuContent']
}) => {
  const menuItems = useGuildMenuItems()
  const { guild, guildReference, teamReference } = useGuild()
  const showTeamMenu =
    mainMenuContent === 'team_switcher_and_team_menu' && teamReference

  if (showTeamMenu) {
    return (
      <CustomDomainSelectedTeamMenu
        selectedTeam={teamReference}
        guild={guild}
        guildReference={guildReference}
      />
    )
  }

  if (!menuItems.length) return null

  return (
    <GuildContext.Provider value={{ guild, guildReference }}>
      <EntityMenu menuItems={menuItems} placement='header' />
    </GuildContext.Provider>
  )
}

export default CustomDomainHeader
